import { memo } from "react";
import { FomoSignal } from "@/hooks/useFomo";

interface FomoSignalsProps {
  signals: FomoSignal[];
  className?: string;
  compact?: boolean;
}

/**
 * Displays FOMO signals in a subtle, non-intrusive way
 * Placed below price, above CTA as per requirements
 */
export const FomoSignals = memo(({ signals, className = "", compact = false }: FomoSignalsProps) => {
  if (signals.length === 0) return null;
  
  if (compact) {
    // Single-line display for product cards
    const signal = signals[0];
    return (
      <div className={`flex items-center gap-1 text-xs text-muted-foreground ${className}`}>
        {signal.icon && <span className="text-xs">{signal.icon}</span>}
        <span>{signal.message}</span>
      </div>
    );
  }
  
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {signals.map((signal, index) => (
        <div 
          key={index}
          className="flex items-center gap-1.5 text-xs text-muted-foreground"
        >
          {signal.icon && <span className="text-xs">{signal.icon}</span>}
          <span>{signal.message}</span>
        </div>
      ))}
    </div>
  );
});

FomoSignals.displayName = "FomoSignals";

interface SingleFomoSignalProps {
  signal: FomoSignal | null;
  className?: string;
}

/**
 * Displays a single FOMO signal - for product cards
 */
export const SingleFomoSignal = memo(({ signal, className = "" }: SingleFomoSignalProps) => {
  if (!signal) return null;
  
  return (
    <div className={`flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground ${className}`}>
      {signal.icon && <span>{signal.icon}</span>}
      <span>{signal.message}</span>
    </div>
  );
});

SingleFomoSignal.displayName = "SingleFomoSignal";

interface ShippingFomoProps {
  isBeforeCutoff: boolean;
  message: string;
  className?: string;
}

/**
 * Shipping-based time FOMO display
 */
export const ShippingFomo = memo(({ isBeforeCutoff, message, className = "" }: ShippingFomoProps) => {
  return (
    <div className={`flex items-center gap-1.5 text-xs ${isBeforeCutoff ? "text-accent" : "text-muted-foreground"} ${className}`}>
      <span>{isBeforeCutoff ? "⚡" : "📦"}</span>
      <span>{message}</span>
    </div>
  );
});

ShippingFomo.displayName = "ShippingFomo";
